import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FolderOpen, FileText, Search, Trash2, Copy, ArrowLeft, Upload, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const isImage = (ct) => (ct || '').startsWith('image/');
const isVideo = (ct) => (ct || '').startsWith('video/');
const fmtSize = (b) => b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round((b || 0) / 1e3)} KB`;
const PAGE = 24;

export default function AdminMediaLibrary() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput); setSkip(0); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE) });
    if (category) params.set('category', category);
    if (q.trim()) params.set('q', q.trim());
    fetch(`${API}/api/uploads/library?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(r.status === 401 ? 'Session expired — log in again' : 'Load failed'); return r.json(); })
      .then(d => { setFiles(d.files || []); setTotal(d.total || 0); setCategories(d.categories || []); setLoading(false); })
      .catch((e) => { setLoading(false); toast.error(e.message || 'Failed to load library'); });
  }, [category, q, skip]);

  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    for (const file of fileList) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entity_type', 'library');
      try {
        const r = await fetch(`${API}/api/uploads/document`, { method: 'POST', body: fd });
        if (!r.ok) throw new Error((await r.json()).detail || 'Upload failed');
        toast.success(`${file.name} uploaded`);
      } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    }
    setUploading(false);
    load();
  };

  const copyUrl = (f) => {
    navigator.clipboard.writeText(`${API}${f.url}`);
    toast.success('URL copied');
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.original_filename}? This can't be undone.`)) return;
    const r = await fetch(`${API}/api/uploads/files/${f.file_id}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      toast.error(r.status === 401 ? 'Session expired — log in again to delete files' : (d.detail || 'Delete failed'));
      return;
    }
    toast.success('File deleted');
    load();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="media-library-page">
      <Toaster position="top-center" richColors />
      <header className="bg-black text-white px-5 py-4 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/fleet-media')} className="text-gray-400 hover:text-white" data-testid="back-to-fleet-media">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <p className="text-[9px] text-red-500 uppercase tracking-[0.3em] font-bold">ACE Admin</p>
              <h1 className="text-lg font-black flex items-center gap-2"><FolderOpen className="h-4 w-4 text-red-500" /> Media Library</h1>
            </div>
          </div>
          <label className="cursor-pointer">
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={e => upload([...e.target.files])} data-testid="library-upload-input" />
            <span className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-lg" data-testid="library-upload-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Files
            </span>
          </label>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-5">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="Search filenames…" className="pl-9 h-9 text-sm bg-white" data-testid="library-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => { setCategory(''); setSkip(0); }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${!category ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-600'}`} data-testid="library-cat-all">All</button>
            {categories.map(c => (
              <button key={c} onClick={() => { setCategory(c); setSkip(0); }} className={`px-3 py-1.5 rounded-full text-[11px] font-bold capitalize ${category === c ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-600'}`} data-testid={`library-cat-${c}`}>{c.replace(/_/g, ' ')}</button>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-400 mb-3" data-testid="library-count">{loading ? 'Loading…' : `${total} file${total !== 1 ? 's' : ''}${category ? ` in ${category}` : ''}`}</p>

        {loading ? (
          <div className="text-center py-16"><div className="h-8 w-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : files.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No files found.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="library-grid">
            {files.map(f => (
              <div key={f.file_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden group" data-testid={`library-file-${f.file_id}`}>
                <a href={`${API}${f.url}`} target="_blank" rel="noreferrer" className="block h-32 bg-gray-100 relative">
                  {isImage(f.content_type) ? (
                    <img src={`${API}${f.url}`} alt={f.original_filename} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  ) : isVideo(f.content_type) ? (
                    <span className="w-full h-full flex items-center justify-center bg-gray-900"><Play className="h-8 w-8 text-red-500" /></span>
                  ) : (
                    <span className="w-full h-full flex items-center justify-center bg-red-50"><FileText className="h-8 w-8 text-red-400" /></span>
                  )}
                </a>
                <div className="p-2.5">
                  <p className="text-[11px] font-semibold truncate" title={f.original_filename}>{f.original_filename || f.file_id}</p>
                  <p className="text-[9px] text-gray-400 mb-2">{fmtSize(f.size)} · <span className="capitalize">{(f.category || '').replace(/_/g, ' ')}</span></p>
                  <div className="flex gap-1">
                    <button onClick={() => copyUrl(f)} className="flex-1 text-[10px] font-bold border border-gray-200 hover:border-black rounded py-1 flex items-center justify-center gap-1" data-testid={`library-copy-${f.file_id}`}><Copy className="h-2.5 w-2.5" /> URL</button>
                    <button onClick={() => remove(f)} className="text-[10px] border border-gray-200 hover:border-red-500 hover:text-red-600 rounded px-2 py-1" data-testid={`library-delete-${f.file_id}`}><Trash2 className="h-2.5 w-2.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > PAGE && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="library-prev">Previous</Button>
            <span className="text-xs text-gray-500">{skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
            <Button variant="outline" size="sm" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)} data-testid="library-next">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
